import { useLayoutEffect, useMemo, useRef } from "react";

/** Stored config still uses `business_key` / `business_time`; labels say primary key / business event time. */

export const LATE_ARRIVING_LABELS: Record<"reject" | "allow_monotonic_only", string> = {
  reject: "Fail",
  allow_monotonic_only: "Skip older rows",
};

type Props = {
  columnNames: string[];
  columnsLoading?: boolean;
  columnsError?: string | null;
  onRefreshColumns?: () => void;
  refreshDisabled?: boolean;

  primaryKeyFields: string[];
  onPrimaryKeyChange: (fields: string[]) => void;

  businessEventTimeField: string;
  onBusinessEventTimeField: (field: string) => void;
  businessEventTimeFallback: "system_time" | "reject";
  onBusinessEventTimeFallback: (v: "system_time" | "reject") => void;

  /**
   * ADR-0094: change_detection persists a deny-list (`untracked_fields`).
   * The UI shows all non-key columns checked by default; checked = tracked,
   * unchecked = in the deny-list. The wire value is the deny-list (columns
   * NOT checked).
   */
  untrackedFields: string[];
  onUntrackedFieldsChange: (fields: string[]) => void;

  /**
   * ADR-0094: Append and Merge reuse the upstream-columns + primary-key
   * picker but skip the MergeHistory-only sections (business event time +
   * tracked-change deny-list). Default true preserves the existing
   * MergeHistory behavior.
   */
  showHistoryOnlyFields?: boolean;

  /**
   * ADR-0103: the "Track changes in" picker (change_detection deny-list) now
   * applies to `merge` as well as `merge_history` — both compare tracked
   * columns. `append` / `replace_snapshot` never show it. Defaults to
   * `showHistoryOnlyFields` so existing callers keep their behavior.
   */
  showChangeTracking?: boolean;

  disabled?: boolean;
  onFieldInteractionEnd?: () => void;
};

export function MergeHistoryFieldPickers(props: Props) {
  const {
    columnNames,
    columnsLoading,
    columnsError,
    onRefreshColumns,
    refreshDisabled,
    primaryKeyFields,
    onPrimaryKeyChange,
    businessEventTimeField,
    onBusinessEventTimeField,
    businessEventTimeFallback,
    onBusinessEventTimeFallback,
    untrackedFields,
    onUntrackedFieldsChange,
    showHistoryOnlyFields = true,
    showChangeTracking = showHistoryOnlyFields,
    disabled,
    onFieldInteractionEnd,
  } = props;

  const pkSet = useMemo(() => new Set(primaryKeyFields), [primaryKeyFields]);
  // ADR-0094: deny-list. A column is TRACKED when it is NOT in the deny-list.
  const untrackedSet = useMemo(() => new Set(untrackedFields), [untrackedFields]);

  const displayColumns = useMemo(() => {
    const s = new Set(columnNames);
    for (const f of primaryKeyFields) if (f) s.add(f);
    if (businessEventTimeField) s.add(businessEventTimeField);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [columnNames, primaryKeyFields, businessEventTimeField]);

  const columnsForChangeComparison = useMemo(
    () => displayColumns.filter((name) => !pkSet.has(name)),
    [displayColumns, pkSet]
  );

  function togglePk(name: string, checked: boolean) {
    const next = new Set(pkSet);
    if (checked) next.add(name);
    else next.delete(name);
    const sorted = Array.from(next).sort((a, b) => a.localeCompare(b));
    onPrimaryKeyChange(sorted);
    // ADR-0094: a column that becomes a key field can't also be in the
    // untracked deny-list — keys never participate in change detection.
    if (checked && untrackedSet.has(name)) {
      onUntrackedFieldsChange(untrackedFields.filter((f) => f !== name));
    }
  }

  /**
   * Toggle the user-facing "tracked" checkbox. Checked = tracked = NOT in
   * the deny-list. So:
   *   checked   → REMOVE from untrackedFields
   *   unchecked → ADD to untrackedFields
   */
  function toggleTracked(name: string, checked: boolean) {
    const next = new Set(untrackedSet);
    if (checked) {
      next.delete(name);
    } else {
      next.add(name);
    }
    onUntrackedFieldsChange(Array.from(next).sort((a, b) => a.localeCompare(b)));
  }

  // "All tracked" means the deny-list contains none of the comparison columns.
  const allTracked =
    columnsForChangeComparison.length > 0 &&
    columnsForChangeComparison.every((n) => !untrackedSet.has(n));
  const someTracked = columnsForChangeComparison.some((n) => !untrackedSet.has(n));
  const selectAllTrackedRef = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    const el = selectAllTrackedRef.current;
    if (!el) return;
    el.indeterminate = !allTracked && someTracked;
  }, [allTracked, someTracked]);

  function setAllTracked(checked: boolean) {
    if (checked) {
      // Track everything → empty deny-list.
      onUntrackedFieldsChange([]);
    } else {
      // Track nothing → every comparison column is in the deny-list.
      onUntrackedFieldsChange(
        [...columnsForChangeComparison].sort((a, b) => a.localeCompare(b))
      );
    }
  }

  const end = onFieldInteractionEnd ?? (() => {});

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="field">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <label className="label" style={{ marginBottom: 0 }}>
            Upstream columns
          </label>
          {onRefreshColumns ? (
            <button
              type="button"
              className="btn btnSm"
              disabled={refreshDisabled || disabled || columnsLoading}
              onClick={() => onRefreshColumns()}
            >
              {columnsLoading ? "Loading…" : "Refresh columns"}
            </button>
          ) : null}
        </div>
        {columnsError ? (
          <div className="muted" style={{ fontSize: 12, color: "var(--danger-text)", marginTop: 4 }}>
            {columnsError}
          </div>
        ) : null}
        {displayColumns.length === 0 && !columnsLoading ? (
          <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
            Run preview on this persist node (or refresh columns) to load field names from the incoming dataset.
          </p>
        ) : null}
      </div>

      <div className="field">
        <label className="label">Primary key</label>
        <span className="help" style={{ display: "block", marginBottom: 6 }}>
          One or more columns that uniquely identify each row.
        </span>
        <div
          className="mono"
          style={{
            display: "grid",
            gap: 4,
            maxHeight: 160,
            overflowY: "auto",
            padding: "6px 8px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          {displayColumns.map((name) => (
            <label key={`pk-${name}`} style={{ display: "flex", alignItems: "center", gap: 8, cursor: disabled ? "default" : "pointer" }}>
              <input
                type="checkbox"
                checked={pkSet.has(name)}
                disabled={disabled}
                onChange={(e) => {
                  togglePk(name, e.target.checked);
                }}
                onBlur={end}
              />
              <span>{name}</span>
            </label>
          ))}
        </div>
      </div>

      {showHistoryOnlyFields ? (
      <>
      <div className="field">
        <label className="label">Business event time</label>
        <span className="help" style={{ display: "block", marginBottom: 6 }}>
          When the business event occurred (drives validity over time). Choose one column.
        </span>
        <div
          className="mono"
          style={{
            display: "grid",
            gap: 4,
            maxHeight: 160,
            overflowY: "auto",
            padding: "6px 8px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: disabled ? "default" : "pointer" }}>
            <input
              type="radio"
              name="biz-event-time"
              checked={businessEventTimeField === ""}
              disabled={disabled}
              onChange={() => {
                onBusinessEventTimeField("");
              }}
              onBlur={end}
            />
            <span className="muted">(none selected)</span>
          </label>
          {displayColumns.map((name) => (
            <label key={`evt-${name}`} style={{ display: "flex", alignItems: "center", gap: 8, cursor: disabled ? "default" : "pointer" }}>
              <input
                type="radio"
                name="biz-event-time"
                checked={businessEventTimeField === name}
                disabled={disabled}
                onChange={() => {
                  onBusinessEventTimeField(name);
                }}
                onBlur={end}
              />
              <span>{name}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="field">
        <label className="label">If the event-time column is null or missing</label>
        <select
          className="select"
          value={businessEventTimeFallback}
          disabled={disabled}
          onChange={(e) => onBusinessEventTimeFallback(e.target.value as "system_time" | "reject")}
          onBlur={end}
        >
          <option value="system_time">Use load time (system time)</option>
          <option value="reject">Reject the row</option>
        </select>
      </div>
      </>
      ) : null}

      {/*
        ADR-0094/0103: change-detection is the per-column tracked set (deny-list).
        Every non-key column is checked by default; unchecking a column adds it
        to `untracked_fields` and excludes it from change comparison. New columns
        added upstream join the tracked set automatically. Applies to both `merge`
        and `merge_history` — both compare tracked columns.
      */}
      {showChangeTracking ? (
      <div className="field">
        <label className="label">Track changes in</label>
        <span className="help" style={{ display: "block", marginBottom: 6 }}>
          When any tracked value differs from the last load, the row is treated as changed.
          Uncheck operational columns (e.g. <code>updated_at</code>) to keep them out of comparison.
        </span>
        <div
          className="mono"
          style={{
            display: "grid",
            gap: 4,
            maxHeight: 160,
            overflowY: "auto",
            padding: "6px 8px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          {columnsForChangeComparison.length > 0 ? (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: disabled ? "default" : "pointer",
                fontWeight: 600,
                borderBottom: "1px solid var(--border)",
                paddingBottom: 4,
                marginBottom: 2,
              }}
            >
              <input
                ref={selectAllTrackedRef}
                type="checkbox"
                checked={allTracked}
                disabled={disabled}
                onChange={(e) => {
                  setAllTracked(e.target.checked);
                }}
                onBlur={end}
              />
              <span>Select all</span>
            </label>
          ) : null}
          {columnsForChangeComparison.map((name) => (
            <label key={`tr-${name}`} style={{ display: "flex", alignItems: "center", gap: 8, cursor: disabled ? "default" : "pointer" }}>
              <input
                type="checkbox"
                checked={!untrackedSet.has(name)}
                disabled={disabled}
                onChange={(e) => {
                  toggleTracked(name, e.target.checked);
                }}
                onBlur={end}
              />
              <span>{name}</span>
            </label>
          ))}
        </div>
        {columnsForChangeComparison.length > 0 && !someTracked ? (
          <div className="help" style={{ color: "var(--danger-text)", marginTop: 6 }}>
            At least one column must be tracked for change detection.
          </div>
        ) : null}
      </div>
      ) : null}
    </div>
  );
}
